import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { PropallyLpApiFactory } from "src/api/apiFactory";
import { HowMaBuilding, HowMaBuildingsSearchRequest, HowMaRoom } from "src/api/generated/lp/api";
import { useApiErrorHandler } from "src/hooks/api-error";
import { FormValues } from "src/hooks/form";

const fp = PropallyLpApiFactory();

/* ------------------------------
 * 共通ユーティリティ
 * ------------------------------ */
function dedupeById<T extends { id: number }>(arr: T[] = []): T[] {
  const seen = new Set<number>();
  return arr.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}

/* ------------------------------
 * 物件概要入力画面のロジック
 * ------------------------------ */
export const useInputPage = () => {
  const { control, setValue, formState: { errors } } = useFormContext<FormValues>();
  return { control, errors, setValue };
};

/* ------------------------------
 * HowMa 建物検索（テキスト検索）
 * - デバウンス
 * - AbortController
 * - 結果の重複除去（id基準）
 * ------------------------------ */
export const useHowmaBuildingsSubmit = (debounceMs = 300) => {
  const { getValues } = useFormContext<FormValues>();
  const { error, clearError, handleError } = useApiErrorHandler();

  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<HowMaBuilding[]>([]);
  const [total, setTotal] = useState<number>(0);

  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const makeBody = useCallback(
    (query?: string): Pick<HowMaBuildingsSearchRequest, "text"> => {
      const q = (query ?? String(getValues("building_name") ?? "")).trim();
      return { text: q };
    },
    [getValues]
  );

  const search = useCallback(
    (query?: string) => {
      const q = (query ?? String(getValues("building_name") ?? "")).trim();

      // 既存デバウンスをクリア
      if (timerRef.current) window.clearTimeout(timerRef.current);

      // 1文字以下は検索せずクリア
      if (q.length < 2) {
        setResults([]);
        setTotal(0);
        clearError();
        if (abortRef.current) abortRef.current.abort();
        return;
      }

      // デバウンス開始
      timerRef.current = window.setTimeout(async () => {
        // 進行中リクエストを中断
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setSending(true);
        clearError();

        try {
          const body = makeBody(q);
          const res = await fp.lpV1HowmaBuildingsPost(body, {
            signal: ctrl.signal,          // ← 中断可能に
            withCredentials: true,
          });

          const list = dedupeById(res.data?.buildings ?? []);
          const count = res.data?.total_count ?? list.length;

          setResults(list);
          setTotal(count);
        } catch (e: unknown) {
          if (e instanceof Error) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
          }
          setResults([]);
          setTotal(0);
          handleError(e);

        } finally {
          setSending(false);
        }
      }, debounceMs) as unknown as number;
    },
    [makeBody, debounceMs, clearError, handleError, getValues]
  );

  const clear = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    setResults([]);
    setTotal(0);
    clearError();
  }, [clearError]);

  return { sending, error, results, total, search, clear };
};

/* ------------------------------
 * HowMa 部屋詳細（建物IDから部屋一覧）
 * - 面積取得に主に利用
 * - デバウンス＋中断
 * - 便利な派生値 firstRoomM2 を提供
 * ------------------------------ */
export const useHowmaRoomSubmit = (debounceMs = 300) => {
  const { error, clearError, handleError } = useApiErrorHandler();

  const [sending, setSending] = useState(false);
  const [resultsRoom, setResults] = useState<HowMaRoom[]>([]);
  const [total, setTotal] = useState<number>(0);

  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  /**
   * 検索実行
   * @param buildingId 必須
   */
  const searchRoom = useCallback(
    (buildingId?: number) => {
      // 既存デバウンスをリセット
      if (timerRef.current) window.clearTimeout(timerRef.current);

      // buildingId が無効ならクリアして終了
      if (!buildingId || Number.isNaN(buildingId)) {
        setResults([]);
        setTotal(0);
        clearError();
        if (abortRef.current) abortRef.current.abort();
        return;
      }

      timerRef.current = window.setTimeout(async () => {
        // 進行中のリクエストを中断
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setSending(true);
        clearError();

        try {
          const res = await fp.lpV1HowmaBuildingsBuildingIdGet(buildingId, {
            signal: ctrl.signal,        // ← 中断可能に
            withCredentials: true,
          });

          const list = res.data?.rooms ?? [];
          const count = res.data?.total_count ?? list.length;

          setResults(list);
          setTotal(count);
        } catch (e: unknown) {
          if (e instanceof Error) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
          }
          setResults([]);
          setTotal(0);
          handleError(e);
        }
        finally {
          setSending(false);
        }
      }, debounceMs) as unknown as number;
    },
    [debounceMs, clearError, handleError]
  );

  const clear = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    setResults([]);
    setTotal(0);
    clearError();
  }, [clearError]);

  // 1件だけ必要なことが多いので、便利な派生値を用意
  const firstRoomM2 = useMemo(() => resultsRoom[0]?.m2, [resultsRoom]);

  return { sending, error, resultsRoom, total, firstRoomM2, searchRoom, clear };
};
